import BaseComponent from "./base_component_controller";

import { useResize } from "hb/insights/use_resize";
import { useChartConfig } from "hb/insights/use_chart_config";
import { extractCascade } from "hb/lib/collection_helpers";
import { isNumberType } from "hb/insights/types";

export default class extends BaseComponent {
  static viewId = "pie";

  static get values() {
    return {
      ...BaseComponent.values,

      querySessionId: String,
      schema: Array,
      results: Array,
      chartConfig: Object,
    };
  }

  static configs = [
    "nameField",
    "valueField",
    { name: "groups", default: "{}", type: "json" },
  ];

  connect() {
    super.connect();

    useChartConfig(this);
  }

  outletsConnected() {
    super.outletsConnected();

    useResize(this, { element: this.outletComponent("chart").chartTarget });
    this.initQuery();
  }

  queryContextDefaults() {
    const { meta } = this.query;
    let config = {};
    const fieldTypes = Object.entries(meta.fieldTypes);

    const nameRes = extractCascade(fieldTypes, ([, t]) => t === "string");

    const valueRes = extractCascade(nameRes[1], ([, t]) => isNumberType(t));

    if (nameRes[0] !== undefined) config.nameField = nameRes[0][0];
    if (valueRes[0] !== undefined) config.valueField = valueRes[0][0];

    return config;
  }

  validate() {
    let { nameField, valueField } = this.config;
    let { results, meta } = this.query;

    if (!nameField || !valueField) {
      this.errorMessage =
        "Select fields in chart config or update data to fit chart";
    } else if (
      ![nameField, valueField].every((f) =>
        f === undefined ? true : meta.has(f),
      )
    ) {
      this.errorMessage = "Unknown field selected";
    } else if (results?.length > 20) {
      this.errorMessage = "Too many results";
    }
  }

  processConfig() {
    const { results } = this.query;
    let { nameField, valueField, groups } = this.config;

    let colorIdx = -1;
    const gatherColor = (n) => {
      colorIdx = (colorIdx + 1) % this.colorValues.length;
      return groups[n]?.color || this.colorValues[colorIdx];
    };

    this.derivedConfig.groupConfig = results.map((r, idx) => {
      const name = r.field(nameField);
      const value = r.field(valueField);
      return { idx, name, value, color: gatherColor(name) };
    });
  }

  renderConfig() {
    const { meta } = this.query;

    const fieldsHTML = meta.fields
      .map((field) =>
        this.comboboxItemTemplate({
          value: field,
          id: field,
          display: field,
        }),
      )
      .join("\n");

    ["nameField", "valueField"].forEach((name) => {
      this.find(`[data-input-name="${name}"] ul`).innerHTML = fieldsHTML;
    });

    this.find(`input[name="nameField"]`).value = this.config.nameField || "";
    this.find(`input[name="valueField"]`).value = this.config.valueField || "";

    this.renderFieldGroups(this.derivedConfig.groupConfig);
  }

  render() {
    let { results } = this.query;
    let { nameField, valueField } = this.config;

    if (!nameField || !valueField || !results) {
      return;
    }

    const defaults = this.defaultChartConfig;
    const options = {
      color: defaults.color,
      series: {
        type: "pie",
        animation: false,
        data: this.derivedConfig.groupConfig.map(({ name, value, color }) => ({
          value,
          name,
          itemStyle: { color },
        })),
      },
      tooltip: {
        ...defaults.tooltip,
        trigger: "item",
      },
    };

    this.outletComponent("chart").setChartOptions(options);
  }
}
